import './styles.css'
import { render } from 'react-dom'
import * as THREE from 'three'
import { useRef, Suspense, useEffect, useState } from 'react'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import { Center, Bounds, Text, Instances, Instance } from '@react-three/drei'
import * as Tone from 'tone'
import Colors from './Colors'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import pack from './pack';
let pause = false
document.body.onkeyup = function (e) {
  if (e.key == " " ||
    e.code == "Space" ||
    e.keyCode == 32
  ) {
    if (clicked) {
      if (started) {
        master.gain.linearRampTo(0,0.3)
        started = false
        pause = true
      } else {
        master.gain.linearRampTo(1,0.5)
        pause = false
        started = true
      }
    }
  }
}
let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let shuffle = (array) => {
  let currentIndex = array.length, randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(fxrand() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}
let xS = rand([1, 2, 4])
let boxes = new Array(rand([888])).fill().map(() => {
  let d = rand([1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 4, 6, 8]) * rand([1, 2, 3, 4, 6, 8]) * 1.75
  return {
    w: d * xS,
    h: d * 1
  }
})
pack(boxes)
Tone.context.lookAhead = 0.25
const ci = randI(Colors.length)
const cols = Colors[ci]
let tempCols = cols
const bgColor = cols.shift()
shuffle(cols)
let chars = 'ABCDĐĐEFFGHIJKLMNOPQQRRRRRRRRRRRSTUVWXYZabcdđefghiiijklmnopqrrrsttuvwxyzАБВГГДЂЕЄЖЖЖЖЗЅИІЈКЛЉМНЊЊОПППРСТЋУФФХЦЧЏШЩЪЫЬЭЮЯЭЮЯабвгґдђеєжжжзѕиіјклљмнњопрстћуффффхцчџшщъыьэюяƠƯ111122222234444445666677777788889999900000‘????’“!!!!”(%)[#]{@@@}///////////&\\\\\\\\\\\<<<<<<-+++÷×÷×÷×=======>>>>>>>>>>®®®®®©$€£¥¢©$€£¥¢:;,.*öÖæææææææøÆÆÆÆÆÆÆÆÆÆØ'
let partSteps = (3 + randI(6)) * 4
let rows = 8 + randI(60)
let columns = 84 - rows
const feats = window.$fxhashFeatures = {
  steps: partSteps,
  cameraX: rand([0, 0,0,0,0, 0.5, 0.5, 1, 1, 2, 2, 3]) * rand([1, -1]),
  bg: bgColor,
  columns,
  rows,
  rotation: randI(100) > 85,
}
class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.onBeforeCompile = (shader) => shader.fragmentShader = `uniform float damp; uniform sampler2D tOld; uniform sampler2D tNew; varying vec2 vUv; vec4 when_gt( vec4 x, float y ) { return max( sign( x - y ), 0.0 );}void main() {vec4 texelOld = texture2D( tOld, vUv ); vec4 texelNew = texture2D( tNew, vUv ); texelOld *= damp * when_gt( texelOld, 0.00001 );gl_FragColor = max(texelNew, texelOld);}`
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => { ref.current.uniforms.damp.value = damp; damp = 1; composer.current.render() }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}

let geo = new THREE.CircleBufferGeometry(rand([0.6, 0.85]), rand([32, 32, 32, 32, 32, 32, 4, 4, 4, 4, 3]))
geo.rotateZ(Math.PI * 0.25)
let started = false
let vol = -10
let randSound = (mod) => {
  let dt = ['16n', '16n', '16n', '16n', '2n']
  delays.forEach((d, i) => {
    d.delayTime[rand(['exponentialRampTo'])](Tone.Time(rand(['16t', '32t'])).toSeconds() * (1 + randI(5)) * 0.25, Tone.Time(rand(dt)).toSeconds())
    d.feedback[rand(['exponentialRampTo'])](0.685 + (randI(7) * 0.05), Tone.Time(rand(dt)).toSeconds())
  })
  arps.forEach((a) => {
    a.volume[rand(['exponentialRampTo', 'linearRampTo'])](rand([-45, vol, vol, vol, vol]), Tone.Time('16t').toSeconds())
    if (randI(100) > 60) {
      a.attack = Tone.Time(rand(['32n', '16n', '64n']))
      a.release = Tone.Time(rand(['32n', '16n', '64n', '8n', '4n']))
    }
  })
  hats.forEach((a) => {
    if (randI(100) > 60) {
      a.attack = Tone.Time(rand(['32n', '16n', '64n']))
      a.release = Tone.Time(rand(['16n', '8n', '4n', '2n']))
    }
  })
  others.forEach((a) => {
    if (randI(100) > 60) {
      a.attack = Tone.Time(rand(['32n', '16n', '64n']))
      a.release = Tone.Time(rand(['32n', '64n', '16n']))
    }
  })
}
let randBpm = () => 30 + randI(50)
Tone.Transport.bpm.value = randBpm()
let clicked = false
export default function App() {
  randSound() || randSound()
  shuffle(notes)
  const size = useWindowSize();
  useEffect(() => { flush() }, [size.height, size.width])
  const click = () => {
    if (started) {
      oct = 0;
      clearTimeout(octTimeout)
      kicking = randI(100) > 95
      hating = randI(100) > 90
      othing = randI(100) > 85
      shuffle(notes)
      randSound();
      Tone.Transport.bpm.value = randBpm()
    }
    flush()
    Tone.start();
    Tone.Transport.start()
    part.start()
    started = true
    clicked = true
  }
  return (
    <div id="main" onClick={() => click()} >
      <Canvas key={size.height} resize={true} flat={true} dpr={[1, 2]} camera={{ near: 0.1, far: 10000, position: [feats.cameraX, 0, 7], fov: 95 }}>
        <Back />
        <Tint />
        <All />
      </Canvas >
    </div >
  )
}
let r = randI(4)
function All() {
  const ref = useRef();
  useFrame(() => {
    ref.current.rotation.z = feats.rotation ? (randI(200) * Math.PI * 0.125) : (r * Math.PI * 0.5)
  })
  return (
    <group ref={ref}>
      <Bounds fit margin={0.44} damping={0}>
        <Center>
          <Center>
            <Texts />
          </Center>
          <Instances limit={100000} geometry={geo}>
            <meshBasicMaterial />
            {boxes.map((_, i) => <Box i={i} key={i} />)}
          </Instances>
        </Center>
      </Bounds>
    </group>
  )
}

let currBox = 0
let boxOffset = 0
let boxShow = false
let currChar = rand(chars)
let modeDir = rand([1, -1])
let modeRot = rand([1, 0])
function Box({ i }) {
  const b = boxes[i]
  const ref = useRef();
  let s = 1
  useFrame(() => {
    if (!boxShow && (i === currBox || i === (currBox + boxOffset))) {
      ref.current.scale.setScalar(0)
    } else {
      ref.current.scale.set(b.w * s, b.h * s, 1)
      if (randI(100) > 30) {
        ref.current.scale.setScalar(0)
      }
    }
  })
  return (
    <Instance ref={ref} color={bgColor} scale={[b.w, b.h, 1]} position={[b.x + (b.w * 0.5), b.y + (b.h * 0.5), 1]} />
  )
}

let randomize = () => {
  spread = [
    rand([1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 4, 5, 6, 7, 8, 10, 12, 14, 20, 8, 6, 16, 15, 1, 1]) * 4,
    rand([1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 4, 5, 6, 7, 8, 10, 12, 14, 20, 8, 6, 16, 15, 1, 1]) * 4,
  ]
  if (randI(100) > 30) {
    modeDir = rand([1, -1])
    modeRot = rand([1, 0])
    boxOffset = rand([0, 0, 0, 1, 1, 1, 1])
    boxShow = randI(100) > 100 ? false : true
    something = randI(7) * rand([1, -1])
    currChar = rand(chars)
  }
  if (randI(100) > 90) {
    mode = randI(modes.length)
    posMode = randI(posModes.length)
  }
  if (randI(100) > 84) {
    tempCols = new Array(1 + randI(Math.floor(cols.length * 0.5))).fill().map((_, i) => rand(cols))
  }
  currBox = Math.min((currBox + rand([1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 3])), boxes.length - 2)
}

function Texts() {
  const ref = useRef();

  useFrame(() => {
    if (!pause) {  
      randomize()
      let b = boxes[currBox]
      ref.current.position.set(b.x, b.y, -1)
    }
  })
  return (
    <group ref={ref} >
      {new Array(rows).fill().map((_, row) => new Array(columns).fill().map((_, col) =>
        <Letter col={col} row={row} key={col} />
      ))
      }
    </group>
  )
}
let modes = [
  (t, c, r) => ((c + (r + t * 9) ^ c - (r + t * 9)) % 7 & 1),
  (t, c, r) => ((c + (r + t * 7) ^ c - (r + t * 9)) % 13 & 1),
  (t, c, r) => ((c + (r + t * 7) ^ c - (r + t * 9)) % 13 & 1),
  (t, c, r) => ((c + (r + t * 6) ^ c - (r + t * 9)) % 9 & 1) - .15,
  (t, c, r) => .04 / Math.sin(3 ** ++c - Math.atan2(c, c * r * r) * 9 - t),
  (t, c, r) => .1 / Math.sin(3 ** ++c - Math.atan2(c, c * r) * 3 - t),
  (t, c, r) => Math.cos(2 * r / c ^ 2 + t),
  (t, c, r) => Math.tan(Math.sin((r + c) / 8 - t * 2) + Math.cos(c / 2 - t * 3)),
  (t, c, r) => [7, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + (0 * 2) * a) ? -a / 3 : b),
  (t, c, r) => [7, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + (0 * 2) * a) ? -a / 3 : b),
  (t, c, r) => [8, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + 0 * a) ? -a / 5 : b),
  (t, c, r) => Math.sin(t * r * c / ((r * columns) + c) * 10),
  (t, c, r) => Math.cos(c / 3 + Math.cos(r / 5 + t * 0.1) * 4) * 2,
  (t, c, r) => Math.min(0, c / r * (Math.hypot(c + Math.cos(t) - (columns * 0.5), r - (rows * 0.5)) - 5)),
  (t, c, r) => Math.min(0, c / r * (Math.hypot(c + Math.cos(t) - (columns * 0.5), r - (rows * 0.5)) - 5)),
]
let something = 1
let spread = [1, 1]
let mode = 0
let posMode = 0

let fr = (Math.PI * 2) / columns
let posModes = [
  (c, r) => ([
    ((c * spread[0]) + (spread[0] * 0.2)),
    ((r * spread[1]) + (spread[1] * 0.2)),
    0
  ]),
  (c, r) => ([
    ((c * spread[0]) + (spread[0] * 0.2)),
    ((r * spread[1]) + (spread[1] * 0.2)) + ((c % 10) * (spread[1])),
    0
  ]),
  (c, r) => ([
    ((c * spread[0]) + (spread[0] * 0.2)),
    ((r * spread[1]) + (spread[1] * 0.2)) - ((c % 10) * (spread[1])),
    0
  ]),
  (c, r) => ([
    ((c * spread[0]) + (spread[0] * 0.2)),
    ((r * spread[1]) + (spread[1] * 0.2)) * Math.sin((Math.PI / columns) * c),
    0
  ]),
  (c, r) => ([
    ((c * spread[0]) + (spread[0] * 0.2)),
    ((r * spread[1]) + (spread[1] * 0.2)),
    0
  ]),
  (c, r) => ([
    (c * spread[0]) + (spread[0] * 0.2),
    (r * spread[1]) + (spread[1] * 0.2) + (Math.sin(c * fr * 0.2) * 30),
    0
  ]),
  (c, r) => ([
    (Math.cos(r * fr) * ((c + 2) * spread[0])) + (boxes[currBox].w * 0.5),
    Math.sin(r * fr) * ((c + 2) * spread[1]) + (boxes[currBox].h),
    0
  ]),
  (c, r) => ([
    (c * spread[0]) + (spread[0] * 0.2),
    (r * spread[1]) + (spread[1] * 0.2) + (Math.sin(c * fr * 2) * 40),
    0
  ]),
  (c, r) => ([
    (c * spread[0]) + (spread[0] * 0.2),
    (r * spread[1]) + (spread[1] * 0.2) + (Math.sin(c * fr * 3) * 30),
    0
  ]),
  (c, r) => ([
    (c * spread[0]) + (spread[0] * 0.2),
    (r * spread[1]) + (spread[1] * 0.2) + (Math.sin(c * fr * 3) * 10),
    0
  ]), (c, r) => ([
    (c * spread[0]) + (spread[0] * 0.2),
    (r * spread[1]) + (spread[1] * 0.2) + (Math.sin(c * fr * 5) * 10),
    0
  ]),
]
new Array(Math.floor(posModes.length - (1 + randI(3)))).fill().map((v, i) => {
  posModes.splice(randI(posModes.length), 1)
})
new Array(Math.floor(modes.length - (1 + randI(3)))).fill().map((v, i) => {
  modes.splice(randI(modes.length), 1)
})
function Letter({ col, row }) {
  const ref = useRef();
  useFrame(() => {
    ref.current.color = tempCols[(col + Math.abs((row * something)) + currBox) % tempCols.length]
    ref.current.text = currChar
    let b = boxes[currBox]
    let bo = boxes[currBox + boxOffset]
    let scale = 0
    let p = posModes[posMode](col, row)
    ref.current.position.set(...p)
    p = [Math.abs(p[0]), Math.abs(p[1])]
    if ((p[0] <= b.w && p[1] <= b.h) || (p[0] <= (bo.w + b.w) && p[1] <= (bo.h + b.h))) {
      if (modeRot) {
        scale = Math.min(1, Math.abs(modes[mode](currBox * 0.2, row, col * modeDir)))
      } else {
        scale = Math.min(1, Math.abs(modes[mode](currBox * 0.2, col * modeDir, row)))
      }
    } else {
      scale = 0
    }
    ref.current.scale.setScalar(scale * globalScale)
    ref.current.rotation.set(
      0, 0, rand([0, Math.PI, 0, Math.PI, 0])
    )
  })
  return (
    <Text
      ref={ref}
      fontSize={11}
      characters={chars}
      color={tempCols[(col + row) % tempCols.length]}
      position={[col, row, 0]}
      rotation={[0, 0, 0]}
      font={'./IBMPlexMono-Light.ttf'}
    >
      {rand(chars)}
    </Text>
  )
}

let showBg = true
function Back() {
  const ref = useRef();
  useFrame((state) => {
    if (!showBg) {
      ref.current.scale.setScalar(0)
    } else {
      ref.current.scale.setScalar(1)
    }
    showBg = false
  })
  return (
    <group ref={ref}>
      <mesh>
        <meshBasicMaterial color={bgColor} side={THREE.BackSide} />
        <octahedronBufferGeometry args={[5000, 3]} />
      </mesh>
    </group>
  )
}

let kickSamples = [
  "./drumSamples/kick-deep.wav",
]
let hatSamples = [
  "./drumSamples/hh.ogg",
  "./drumSamples/hihat-digital.wav",
  "./drumSamples/hihat8.wav",
  "./drumSamples/hihat13.wav",
  "./drumSamples/hihat-analog.wav",
]
let otherSamples = [
  "./drumSamples/hh.ogg",
  "./drumSamples/hihat-digital.wav",
]
let master = new Tone.Gain(1).toDestination()
let kicks = kickSamples.map((d, di) =>
  new Tone.Sampler({
    'C3': d,
  }, {
    'attack': 0.01,
    'release': Tone.Time('32n'),
  }).connect(new Tone.Panner(-0.4).connect(master))
)
let hatGains = []
let hats = hatSamples.map((d, di) => {
  let g = new Tone.Gain(1).connect(new Tone.Panner(0.1).connect(master))
  let s = new Tone.Sampler({
    'C3': d,
  }, {
    'attack': Tone.Time('64n'),
    'release': Tone.Time('32n'),
  }).connect(g)
  hatGains.push(g)
  return s
})
let otherGains = []
let others = otherSamples.map((d, di) => {
  let g = new Tone.Gain(1).connect(new Tone.Panner(-0.3).connect(master))
  let s = new Tone.Sampler({
    'C3': d,
}, {
    'attack': Tone.Time('64n'),
    'release': Tone.Time('32n'),
  }).connect(g)
  otherGains.push(g)
  return s
})

let arpAmt = 7
let arps = new Array(arpAmt).fill().map(() =>
  new Tone.Sampler({
    'A1': 'A1.[mp3|ogg]',
    'A#1': 'As1.[mp3|ogg]',
    'B1': 'B1.[mp3|ogg]',
    'C2': 'C2.[mp3|ogg]',
    'C#2': 'Cs2.[mp3|ogg]',
    'D2': 'D2.[mp3|ogg]',
    'D#2': 'Ds2.[mp3|ogg]',
    'E2': 'E2.[mp3|ogg]',
    'F2': 'F2.[mp3|ogg]',
    'G2': 'G2.[mp3|ogg]',
    'G#1': 'Gs1.[mp3|ogg]',
  }, {
    'attack': Tone.Time('32n'),
    'release': Tone.Time('16n'),
    'baseUrl': './casio/'
  })
)
let scales = [
  'C',
  'D',
  'E',
  'F',
  'G',
  'A',
  'B',
]
let notes = []
scales.forEach((s) => [0, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2].forEach((o) => {
  notes.push(s + o)
}))
let st = Tone.Time('32n')
let partData = []
new Array(partSteps).fill().map((s, si) => {
  arps.forEach((a, ai) => {
    if (randI(10) > 5) {
      partData.push({
        time: si * st,
        ai,
        si,
        pi: partData.length,
        drum: false
      })
    }
  })
  partData.push({
    time: si * st,
    ai: 0,
    si,
    pi: partData.length,
    drum: true
  })
})
let count = 0;
let oct = 0
let lastOct = 0
let globalScale = 1
let kicking = randI(100) > 90
let hating = randI(100) > 90
let othing = randI(100) > 90
let kickMod = 4
let hatMod = 1
let otherMod = 2
let octTimeout = null
let sideChain = (offset) => {
  let t = 100 + offset
  gains.forEach((g) => {
    g.gain.linearRampTo(0.3, 0.04)
    setTimeout(() => g.gain.linearRampTo(1, Tone.Time('32n')), t)
  })
  hatGains.forEach((g) => {
    g.gain.linearRampTo(0.3, 0.04)
    setTimeout(() => g.gain.linearRampTo(1, Tone.Time('32n')), t)
  })
  otherGains.forEach((g) => {
    g.gain.linearRampTo(0.3, 0.04)
    setTimeout(() => g.gain.linearRampTo(1, Tone.Time('32n')), t)
  })
}
let flush = () => {
  count = 0
  damp = 0
  showBg = true
  currBox = 0
}
let part = new Tone.Part(((time, { ai, si, pi, drum }) => {
  if (drum) {
    if (si % kickMod === 0 && kicking) {
      if (si === 0) {
        damp = 0
        showBg = true
        currBox = 0
      }
      kicks[randI(kicks.length)].triggerAttackRelease('C3', Tone.Time('4n').toSeconds() * rand([0.55, 0.65]), time, rand([0.1, 0.1, 0.15, 0.2, 0.25]) * 0.7);
      if (randI(100) > 90) {
        sideChain(st.toMilliseconds())
        kicks[randI(kicks.length)].triggerAttackRelease(rand(['C3']), Tone.Time('4n').toSeconds() * rand([0.55, 0.65]), time + st, rand([0.1, 0.15, 0.2, 0.25]) * 0.6);
      }
      sideChain(0)
    }
    if (hating && (si % hatMod === 0 || (randI(100) > 90 && ((si + 1) % hatMod === 0)))) {
      hats[rand([0, 0, 0, 0, 0, 1, 2])].triggerAttackRelease(rand(['C3']), Tone.Time('2n').toSeconds() * rand([0.25, 0.35, 0.15]), time, rand([0.075, 0.1, 0.15, 0.2, 0.3, 0.4]) * 0.2);
    }
    if (((si + 2) % otherMod === 0) && othing) {
      others[si % others.length].triggerAttackRelease(rand(['C3']), Tone.Time('1m').toSeconds() * rand([0.25, 0.1]), time, rand([0.075, 0.1, 0.15, 0.2, 0.25, 0.3]) * 0.15);
    }
  } else {
    count++
    let n = Tone.Frequency(notes[(si + ai) % notes.length])
    let n2 = Tone.Frequency(notes[(si + ai) % notes.length]).transpose(12 * oct)
    if ((count > (Math.floor(Tone.Transport.bpm.value * 6)) && (pi === 0)) || (((pi === 0)) && randI(300) < 2)) {
      if (randI(100) > 75) {
        setTimeout(() => {
          kicking = rand([true, false, false, false])
          hating = rand([true, true, false, false, false])
          othing = rand([true, false, false])
          kickMod = rand([2, 4, 8, 16, 4, 8, 16])
          hatMod = rand([1, 1, 1, 1, 2])
          otherMod = rand([2, 3, 4, 6, 8])
        }, st.toMilliseconds() * partSteps)
      }
      flush()
      lastOct = oct
      oct = (!oct ? rand([1, -1, -2, 1, -1, -2, -3, -1, -2, -3, 1]) : rand([0, 0, oct]))
      if (randI(100) > 70) {
        octTimeout = setTimeout(() => {
          oct = lastOct
          flush()
        }, st.toMilliseconds() * partSteps * 2)
      }
      if (randI(100) > 80) {
        randSound()
      }
    }
    arps[ai].triggerAttackRelease(n, Tone.Time('64n').toSeconds() * rand([0.5, 0.65]) * 0.8, time, rand([0.15, 0.2, 0.3, 0.7]) * 1.1);
    if (oct) {
      arps[(ai + 1) % arps.length].triggerAttackRelease(n2, Tone.Time('64n').toSeconds() * rand([0.8, 0.9, 1]) * 0.7, time, rand([0.2, 0.3, 0.35, 0.4, 0.5, 0.6, 0.7, 0.75, 0.8]));
    }
  }
}), partData)
part.loopEnd = st * partSteps
part.loop = true
let gains = []
let delays = []
let panRange = 1.6
arps.forEach((a, i) => {
  let p = new Tone.Panner((-panRange * 0.5) + ((panRange / arps.length) * i)).connect(master)
  let g = new Tone.Gain(1).connect(p)
  let d = new Tone.PingPongDelay(rand(['1t', '4t', '4t', '2t', '2t', '2t', '8t', '16t', '16n', '32n']), rand([0.83, 0.88, 0.92, 0.94, 0.96, 0.95])).connect(g)
  a.volume.value = rand([-40, -40, -40, vol, vol, vol])
  d.wet.value = rand([0.2, 0.5, 0.4]) * 0.25
  delays.push(d)
  gains.push(g)
  a.connect(d)
})

let damp = 0.5
document.getElementById('root').style.background = bgColor
render(
  <Suspense fallback={null}>
    <App />
  </Suspense>,
  document.getElementById('root')
)
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}
console.log('%c ⛵⛵ @ramioso 2022-06-17 ⛵⛵', 'color: orange');